import { normalizeType } from './util';

const reservedTypes = Object.create(null);

reservedTypes.checkbox = true;
reservedTypes.date = true;
reservedTypes.max = true;
reservedTypes.min = true;
reservedTypes.month = true;
reservedTypes.number = true;
reservedTypes.pattern = true;
reservedTypes.radio = true;
reservedTypes.range = true;
reservedTypes.required = true;
reservedTypes.text = true;
reservedTypes.time = true;
reservedTypes.url = true;
reservedTypes.week = true;

export class ValidatorRegistrationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ValidatorRegistrationError';
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, this.constructor);
        } else {
            this.stack = (new Error(message)).stack;
        }
    }
}

function unregisteredValidatorFactory(type) {
    return function unregisteredValidator() {
        if (console && console.warn) {
            console.warn(`Called validator for unregistered type ${type}.`);
        }
        return false;
    };
}

export class ValidationService {
    constructor() {
        this._validations = Object.create(null);
    }

    /**
     * Check if a validator has already been registered for a given type.
     *
     * @param {string} type Type to check for a registered validator.
     * @returns {boolean} True if validator has been registered.
     */
    isRegistered(type) {
        return this._isRegistered(normalizeType(type));
    }

    _isRegistered(type) {
        return !!this._validations[type];
    }

    /**
     * Register a new validation type with the service.
     *
     * @param {string|string[]} type A string or list of strings to register the validator as
     * @param {function} validator The validator function to register
     * @param {object} opts Options to further describe the validator
     * @param {boolean} opts.isAsync  A flag to indicate if validation async or not
     * @returns {void}
     */
    register(type, validator, opts = null) {
        if (typeof validator !== 'function') {
            throw new ValidatorRegistrationError(
                `Validator for ${type} must be a function.`
            );
        }

        if (Array.isArray(type)) {
            type.forEach(t => this._register(normalizeType(t), validator, opts));
        } else {
            this._register(normalizeType(type), validator, opts);
        }
    }

    _register(type, fn, opts) {
        if (this._isRegistered(type)) {
            throw new ValidatorRegistrationError(
                `Validator for ${type} already registered.`
            );
        } else if (reservedTypes[type]) {
            throw new ValidatorRegistrationError(
                `\`${type}\` is reserved in Angular.`
            );
        }

        this._validations[type] = {
            validatorFunc: fn,
            isAsync: !!(opts && opts.isAsync) || false
        };
    }

    /**
     * Get an array of all validators registered with the service.
     *
     * @returns {string[]} Array of all validator types registered with service
     */
    getRegistered() {
        return Object.keys(this._validations);
    }

    /**
     * Validate a value against a known type. Always returns false is the
     * validation type is not registered.
     *
     * @param {string} type Validation type to check.
     * @param {any} value Value to validate.
     * @return {boolean} True if value passed validation.
     */
    validate(type, value) {
        type = normalizeType(type);
        if (!this._isRegistered(type)) {
            if (console && console.warn) {
                console.warn(`No validator registered for ${type}.`);
            }
            return false;
        }
        return this._validations[type].validatorFunc(value);
    }

    /**
     * Retrieve a validator function for a given type. If the type is not
     * registered, a validator function which always returns false will be
     * returned instead.
     *
     * @param {string} type Type to retrieve validator for
     * @returns {Function} A validator function
     */
    validatorFor(type) {
        const normalized = normalizeType(type);
        return (
            (this._validations[normalized] &&
            this._validations[normalized].validatorFunc) ||
            unregisteredValidatorFactory(type)
        );
    }

    /**
     * Returns the validator descriptor object for validator type
     *
     * @param {string} type Type to retrieve validator for
     * @returns {object} Validator descriptor object { validatorFunc: fn, isAsync: boolean }
     */
    _validatorDescFor(type) {
        return (
            this._validations[normalizeType(type)] ||
            unregisteredValidatorFactory(type)
        );
    }
}
